import { useEffect, useMemo, useState } from 'react';
import { subscribeSchoolTimeBranches, subscribeTenantDocument } from '@services/registry';

function normalizeTenantBranches(attendanceConfig) {
    const raw = Array.isArray(attendanceConfig?.branches) ? attendanceConfig.branches : [];
    return raw
        .filter(Boolean)
        .map((b, idx) => ({
            id: b?.id || `branch_${idx + 1}`,
            branchName: b?.branchName || '',
            address: b?.address || '',
            effectiveFrom: b?.effectiveFrom || null,
            schedule: b?.schedule && typeof b.schedule === 'object' ? b.schedule : {},
        }));
}

/**
 * Campus/branch rows for class assignment: prefers `schoolTime/{tenantId}/branches/*`,
 * falls back to `tenants.attendanceConfig.branches` (legacy / not yet migrated).
 */
export function useCampusBranches({ tenantId }) {
    const [subRows, setSubRows] = useState([]);
    const [tenantAttendance, setTenantAttendance] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!tenantId) {
            setSubRows([]);
            setTenantAttendance(null);
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        const unsubBranches = subscribeSchoolTimeBranches({
            tenantId,
            onNext: (rows) => {
                setSubRows(rows);
                setLoading(false);
            },
            onError: () => {
                setLoading(false);
            },
        });
        const unsubTenant = subscribeTenantDocument({
            tenantId,
            onNext: (t) => {
                setTenantAttendance(t?.attendanceConfig ?? null);
            },
            onError: () => {},
        });
        return () => {
            unsubBranches();
            unsubTenant();
        };
    }, [tenantId]);

    /** Stable string so tenant snapshots with the same data do not churn references. */
    const tenantBranchesKey = useMemo(() => {
        const u = tenantAttendance?.updatedAt;
        const um = typeof u?.toMillis === 'function' ? u.toMillis() : u ?? '';
        const br = tenantAttendance?.branches;
        return `${um}::${JSON.stringify(br || [])}`;
    }, [tenantAttendance]);

    const tenantRows = useMemo(() => normalizeTenantBranches(tenantAttendance), [tenantBranchesKey]);

    const branches = useMemo(() => {
        if (subRows.length) return subRows;
        return tenantRows;
    }, [subRows, tenantRows]);

    const remoteEtag = useMemo(() => {
        if (subRows.length) {
            return subRows.map((r) => `${r.id}:${r.branchName || ''}:${r.effectiveFrom || ''}`).join('|');
        }
        return `t:${tenantBranchesKey}`;
    }, [subRows, tenantBranchesKey]);

    return {
        branches,
        loading,
        remoteEtag,
        hasCanonicalSchoolTimeBranches: subRows.length > 0,
    };
}
